import { Result } from '@kit/shared/result';
import { SignedUrlExpiry, StorageBuckets } from '@kit/shared/storage';

import type { DancerMediaContext } from './dancer-media.context';

/**
 * Get a signed URL for viewing/downloading media
 *
 * Uses centralized StorageUrlService for consistent URL generation.
 */
export async function getSignedUrl(
  ctx: DancerMediaContext,
  storagePath: string,
  expiresIn: number = SignedUrlExpiry.IMMEDIATE_DISPLAY,
): Promise<Result<string>> {
  return ctx.storageService.getSignedUrl(
    StorageBuckets.DANCER_MEDIA,
    storagePath,
    { expiresIn },
  );
}

/**
 * Batch-sign multiple dancer-media storage paths in ONE round-trip.
 *
 * Returns a `path → signedUrl` map; paths that fail to sign (e.g. RLS denies,
 * object missing) are simply omitted, so callers keep their existing value.
 * Signing is RLS-enforced by Supabase Storage, so only objects the caller can
 * SELECT are returned. Prefer this over N single `getSignedUrl` calls.
 */
export async function getSignedUrls(
  ctx: DancerMediaContext,
  storagePaths: string[],
  expiresIn: number = SignedUrlExpiry.IMMEDIATE_DISPLAY,
): Promise<Map<string, string>> {
  const map = new Map<string, string>();
  if (storagePaths.length === 0) return map;

  const batch = await ctx.storageService.getBatchSignedUrls(
    StorageBuckets.DANCER_MEDIA,
    storagePaths,
    { expiresIn },
  );
  for (const { path, signedUrl } of batch) {
    if (signedUrl) map.set(path, signedUrl);
  }
  return map;
}
